/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Box } from '@chakra-ui/react';
import { checkLogin } from '../utils';
import Header from './Header';
import Footer from './Footer';
import AiPanel, { AI_PANEL_WIDTH } from './AiPanel/AiPanel';
import { AiPanelProvider, useAiPanel } from './AiPanel/AiPanelContext';

// The header is pinned to the top of the viewport, which takes it out of
// document flow — so an equal-height spacer has to hold its place or every
// page's first rows would render underneath it.
//
// The height is *measured* rather than hardcoded because the header is not a
// fixed size: it grows by another 40px when the maintenance banner is showing
// (Header renders it when the status query returns 0), and collapses to 0 when
// `hiddenHeader` is set. A constant would silently mis-offset in both cases,
// and this ships to screens that can't all be checked by hand.
function useMeasuredHeight(ref, enabled) {
	const [height, setHeight] = useState(0);

	useLayoutEffect(() => {
		if (!enabled) {
			setHeight(0);
			return undefined;
		}
		const el = ref.current;
		if (!el) return undefined;

		const measure = () => setHeight(el.getBoundingClientRect().height);
		measure();

		// ResizeObserver catches the banner appearing/disappearing and any
		// reflow from responsive wrapping. Guarded because it is absent in
		// older browsers and in jsdom, where the initial measure still applies.
		if (typeof ResizeObserver === 'undefined') {
			window.addEventListener('resize', measure);
			return () => window.removeEventListener('resize', measure);
		}
		const observer = new ResizeObserver(measure);
		observer.observe(el);
		return () => observer.disconnect();
	}, [ref, enabled]);

	return height;
}

// Inner half of Layout: needs to be a separate component so it can call
// useAiPanel(), which is only valid *inside* AiPanelProvider.
function LayoutContent(props) {
	const { isOpen } = useAiPanel();
	const headerRef = useRef(null);
	const headerVisible = props?.hiddenHeader !== true;
	const headerHeight = useMeasuredHeight(headerRef, headerVisible);

	return (
		<>
			{/* `hidden-print` on the wrapper as well as on Header's own root: a
			    fixed element would otherwise repeat on every printed page, and
			    this app prints notes and reports heavily. */}
			{/* zIndex uses Chakra's `sticky` token (1100), deliberately below
			    `modal` (1400), `popover` (1500) and `toast` (1700) so dialogs,
			    menus and toasts still layer above the header instead of being
			    clipped behind it. */}
			<Box ref={headerRef} className="hidden-print" position="fixed" top="0" left="0" right="0" zIndex="sticky">
				<Header hidden={props?.hiddenHeader} />
			</Box>
			{/* Holds the space the fixed header no longer occupies. */}
			<Box className="hidden-print" h={`${headerHeight}px`} flexShrink={0} />
			{/* The panel is docked, not overlaid — page content shrinks by exactly
			    the panel's width so nothing sits underneath it. Only from `md` up:
			    on small screens the panel takes the full width instead, where
			    splitting the view would leave neither side usable. */}
			<Box minH="calc(100vh - 80px)" transition="margin-right 0.15s ease-out" mr={{ base: 0, md: isOpen ? AI_PANEL_WIDTH : 0 }}>
				{props?.children ? props?.children : null}
			</Box>
			<Footer hidden={props?.hiddenFooter} />
			{/* Passed the measured height so the panel sits flush under the header
			    instead of assuming a constant 40px, which breaks when the
			    maintenance banner is showing. */}
			<AiPanel topOffset={headerHeight} />
		</>
	);
}

function Layout(props) {
	const isLogin = checkLogin();
	const history = useHistory();

	useEffect(() => {
		if (!isLogin) {
			history.push('/login');
		}
	}, []);

	return (
		<AiPanelProvider>
			<LayoutContent {...props} />
		</AiPanelProvider>
	);
}

export default Layout;
